import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { sincronizarSenhaComMemberkit } from "../lib/memberkit";
import { Aviso, Carregando, LogoCF } from "../componentes/ui";
import { esquecerPermissaoDeSenha, podeDefinirSenha } from "../lib/linkDeEmail";

// O link do e-mail — recuperação OU convite de primeiro acesso — abre esta rota
// já com sessão: o cliente do Supabase troca o token da URL por sessão sozinho.
//
// Mas SESSÃO NÃO PROVA QUE VEIO DO LINK: quem já está logado tem sessão igual.
// Por isso a guarda pergunta a `lib/linkDeEmail` se esta carga da página nasceu
// de um link, e não só se existe sessão. A premissa antiga ("sem sessão, a pessoa
// chegou aqui direto") deixava qualquer aluno logado definir senha nova sem saber
// a atual, só digitando a URL — medido em produção em 18/08.
export default function RedefinirSenha() {
  const { sessao, carregando } = useAuth();
  const navigate = useNavigate();
  const [senha, setSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (senha !== confirma) {
      setErro("As duas senhas não conferem.");
      return;
    }
    setOcupado(true);
    const { error } = await supabase.auth.updateUser({ password: senha });
    setOcupado(false);
    if (error?.code === "same_password") {
      // Acontece de verdade: a pessoa pede o link achando que esqueceu e
      // digita a senha que já tinha. Dizer "link expirado" aqui mandava ela
      // repetir o ciclo inteiro sem sair do lugar (revisão de 11/08).
      setErro("Essa já é a sua senha atual — você pode entrar com ela agora.");
    } else if (error) {
      setErro("Não foi possível salvar a senha nova. O link pode ter expirado — peça outro.");
    } else {
      // A permissão era para UMA troca, e ela já aconteceu. Limpar aqui, antes da
      // sincronização, porque a senha mudou mesmo se o Memberkit falhar.
      esquecerPermissaoDeSenha();
      // Senha nova tem de valer nas aulas também. Aqui COM await, ao contrário
      // do login: a pessoa acabou de definir a senha e vai usá-la no Memberkit
      // em seguida; esperar alguns segundos é melhor que ela descobrir na aula
      // que a senha antiga ainda vale. Se falhar, o mk_senha_em fica nulo e o
      // próximo login reempurra.
      await sincronizarSenhaComMemberkit(senha, "redefinicao");
      navigate("/", { replace: true });
    }
  }

  if (carregando) return <Carregando />;

  if (!sessao || !podeDefinirSenha(sessao.user.id)) {
    // Dois motivos diferentes caem aqui, e dizer a mesma frase para os dois
    // manda a pessoa para o lugar errado: quem não tem sessão pede o link na
    // entrada; quem está logado pede no próprio perfil.
    const logado = Boolean(sessao);
    return (
      <Aviso
        titulo={logado ? "Esta página precisa de um link" : "Link inválido ou expirado"}
        acao={
          <Link to={logado ? "/perfil" : "/entrar"} className="pill-cta">
            {logado ? "Ir para o meu perfil" : "Ir para a entrada"}
          </Link>
        }
      >
        {logado
          ? "Definir uma senha aqui só vale vindo do link que a gente manda por e-mail. Peça o seu em Perfil → Conta e acesso."
          : "Peça um novo link de redefinição na tela de entrada — os links valem por pouco tempo, de propósito."}
      </Aviso>
    );
  }

  const campo =
    "h-12 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none";

  return (
    <div className="painel-navy flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <LogoCF className="mx-auto mb-7 h-8" />
        <div className="surface p-7 sm:p-8">
          <h1 className="text-2xl font-bold text-titulo">Defina a sua nova senha</h1>
          <p className="mt-1 text-sm text-cinza">
            Ela vale para o portal e para as aulas — a gente sincroniza as duas.
          </p>

          <form onSubmit={enviar} className="mt-5 flex flex-col gap-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-tinta">Nova senha</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className={campo}
              />
              <span className="mt-1.5 block text-xs text-cinza">Mínimo de 8 caracteres.</span>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-tinta">Repita a senha</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirma}
                onChange={(e) => setConfirma(e.target.value)}
                className={campo}
              />
            </label>

            {erro && (
              <p role="alert" className="rounded-xl bg-erro-fundo p-3.5 text-sm leading-relaxed text-erro-tinta">
                {erro}
              </p>
            )}

            <button type="submit" disabled={ocupado} className="pill-cta w-full py-3.5 text-base">
              {ocupado ? "Salvando…" : "Salvar e entrar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
